import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import SpaceParticles from "../../components/Particles/SpaceParticles";
import { getBlogPosts } from "../../firebase/blogServices";
import { FaArrowRight, FaTag, FaTimes } from "react-icons/fa";
import "./Blog.css";
import ReactGA from "react-ga4";
import { slugify } from "../../utils/slugify";

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tagFilter = searchParams.get("tag");

  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const blogPosts = await getBlogPosts();
        setPosts(blogPosts);

        // Apply tag filtering if a tag is specified
        if (tagFilter) {
          const filtered = blogPosts.filter(
            (post) =>
              post.tags &&
              post.tags.some(
                (tag) => tag.toLowerCase() === tagFilter.toLowerCase()
              )
          );
          setFilteredPosts(filtered);

          // Track tag filter usage
          ReactGA.event({
            category: "User",
            action: "Filtered Blog By Tag",
            label: tagFilter,
          });
        } else {
          setFilteredPosts(blogPosts);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError(
          "Falha ao carregar os posts do blog. Por favor, tente novamente mais tarde."
        );
        setLoading(false);
      }
    };

    fetchPosts();
  }, [tagFilter]);

  // Track blog visit
  useEffect(() => {
    ReactGA.event({
      category: "User",
      action: "Visited Blog Page",
    });
  }, []);

  // Format date to Brazilian format
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Truncate text to a specific length
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + "...";
  };

  // Clear tag filter
  const clearTagFilter = () => {
    setSearchParams({});
  };

  return (
    <div className="blog-page">
      <Navbar />
      <main className="blog-main">
        <section className="blog-hero">
          <h1 className="blog-hero-title">Blog</h1>
          <p className="blog-hero-subtitle">
            Compartilhando conhecimento e experiências sobre o universo da
            tecnologia e desenvolvimento de software
          </p>

          {tagFilter && (
            <div className="blog-tag-filter">
              <span className="filter-label">Filtrando por:</span>
              <div className="active-filter">
                <FaTag />
                <span>{tagFilter}</span>
                <button onClick={clearTagFilter} title="Remover filtro">
                  <FaTimes />
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="blog-posts-section">
          <div className="blog-posts-container">
            {loading ? (
              <div className="blog-posts-loading">
                <div className="loading-spinner"></div>
                <p>Carregando artigos...</p>
              </div>
            ) : error ? (
              <div className="blog-posts-error">
                <p>{error}</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="blog-posts-empty">
                <p>Nenhum artigo encontrado com a tag "{tagFilter}".</p>
                <button
                  onClick={clearTagFilter}
                  className="reset-filter-button"
                >
                  Ver todos os artigos
                </button>
              </div>
            ) : (
              <div className="blog-posts-grid">
                {filteredPosts.map((post, index) => (
                  <Link
                    to={`/blog/${slugify(post.title)}`}
                    className="blog-post-card"
                    key={post.id}
                    style={{ "--card-index": index + 1 }}
                  >
                    <div className="blog-post-image">
                      <img
                        src={post.imageUrl || "/images/blog-placeholder.jpg"}
                        alt={post.title}
                        onError={(e) => {
                          e.target.src = "/images/blog-placeholder.jpg";
                        }}
                      />
                    </div>
                    <div className="blog-post-content">
                      <span className="blog-post-date">
                        {formatDate(post.createdAt)}
                      </span>
                      <h3 className="blog-post-title">{post.title}</h3>
                      <p className="blog-post-excerpt">
                        {truncateText(post.excerpt || post.content, 140)}
                      </p>
                      <span className="blog-post-link">
                        Ler mais <FaArrowRight />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <SpaceParticles />
    </div>
  );
};

export default Blog;
